import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import {
  combineLatest,
  map,
  Observable,
  startWith,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { FoodItemInflowModel } from 'src/app/models/food-item-inflow.model';
import { FoodItemModel } from 'src/app/models/food-item.model';
import { AyBService } from 'src/app/services/api/ayb.service';
import { SalesService } from 'src/app/services/api/sales.service';
import { NotificationService } from 'src/shared/notification.service';
import Swal from 'sweetalert2';
import { FormIngresoService } from '../../services/forms/form-ingreso.service';
import { DialogIngresoInsumoComponent } from './dialog-ingreso-insumo/dialog-ingreso-insumo.component';

@Component({
  selector: 'app-ingreso',
  templateUrl: './ingreso.component.html',
  styleUrls: ['./ingreso.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IngresoComponent implements OnInit, OnDestroy {
  private _unsubscribeOnDestroy$: Subject<boolean> = new Subject<boolean>();
  isLoading = true;

  selectedFoodInflowType:
    | 'Con documentos contables'
    | 'Sin File'
    | 'Sin Factura' = 'Con documentos contables';
  FOOD_INFLOW_CATEGORIES = [
    'Con documentos contables',
    'Sin File',
    'Sin Factura',
  ];

  currentDate: any;
  servicesSourcing: any = [];

  displayedColumns = [
    'nombre',
    'UM',
    'cantidad',
    'costoUnitario',
    'costoTotal',
    'modificar',
    'eliminar',
  ];

  addedIngreso: any[] = [];

  foodItemControl = new FormControl<string | any>('', { nonNullable: true });
  providerControl = new FormControl<string | any>('');
  fileControl = new FormControl<string | any>('');

  foodItems: FoodItemModel[] = [];
  proveedores: any = [];

  autocompleteFoodItem$!: Observable<any[]>;
  autocompleteProviders$!: Observable<any[]>;

  filesNumbers: {
    id: string;
    fileNumber: string;
    fileExtend?: string | null;
  }[] = [];
  autocompleteFiles$!: Observable<
    { id: string; fileNumber: string; fileExtend?: string | null }[]
  >;

  ubicacionControl = new FormControl('');
  ubicaciones: string[] = ['Cuzco', 'Huaraz', 'Lima'];

  dataSource = new MatTableDataSource(this.addedIngreso);

  isEditMode: boolean = false;
  isWarning: boolean = false;
  ingresoId: string | null = null;
  inflow: FoodItemInflowModel | null = null;

  @ViewChild('autosize') autosizeObservationField!: CdkTextareaAutosize;

  ingresoForm!: FormGroup;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private _ngZone: NgZone,
    public dialog: MatDialog,
    private readonly cdRef: ChangeDetectorRef,
    private readonly salesService: SalesService,
    private readonly alimentosService: AyBService,
    private readonly formIngresoService: FormIngresoService,
    private readonly notificationService: NotificationService,
  ) {
    this.ingresoForm = this.formIngresoService.getIngresoForm();
  }

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.get('id')) {
      this.isEditMode = true;
      this.ingresoId = this.route.snapshot.paramMap.get('id');
    }

    combineLatest([
      this.alimentosService.queryGetAllFoodItemsSku(),
      this.alimentosService.queryGetAllProvider(),
      this.salesService.getAllFilesNumbers(),
    ])
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe(([insumosResponse, proveedoresResponse, filesResponse]) => {
        this.foodItems = insumosResponse.getAllFoodItemsSku?.insumos || [];
        this.proveedores = proveedoresResponse.getAllProvider;

        const rawFiles = filesResponse.getAllFilesNumbers || [];
        const uniqueFilesMap = new Map<
          string,
          { id: string; fileNumber: string; fileExtend?: string | null }
        >();
        for (const fileItem of rawFiles) {
          if (fileItem?.fileNumber) {
            const uniqueKey = `${fileItem.fileNumber.trim()}#${(fileItem.fileExtend || '').trim()}`;
            if (!uniqueFilesMap.has(uniqueKey)) {
              uniqueFilesMap.set(uniqueKey, fileItem);
            }
          }
        }
        const filesList = Array.from(uniqueFilesMap.values());
        filesList.sort((firstItem, secondItem) => {
          const numberComparison = (secondItem.fileNumber || '').localeCompare(
            firstItem.fileNumber || '',
          );
          if (numberComparison !== 0) {
            return numberComparison;
          }
          return (secondItem.fileExtend || '').localeCompare(
            firstItem.fileExtend || '',
          );
        });
        this.filesNumbers = filesList;

        if (this.isEditMode && !!this.ingresoId)
          this.getIngresoById(this.ingresoId);

        this.isLoading = false;
        this.cdRef.markForCheck();
      });

    this.autocompleteFoodItem$ = this.foodItemControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const nombreInsumo =
          typeof value === 'string' ? value : value?.nombreInsumo;
        return nombreInsumo
          ? this._filterAutocompleteFoodItem(nombreInsumo as string)
          : this.foodItems.slice();
      }),
    );

    this.autocompleteFiles$ = this.fileControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const input =
          typeof value === 'string' ? value : this.getFileNumberText(value);
        return input
          ? this._filterAutocompleteFile(input as string)
          : this.filesNumbers.slice();
      }),
    );

    this.autocompleteProviders$ = this.providerControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const razonSocial =
          typeof value === 'string' ? value : value?.razonSocial;
        return razonSocial
          ? this._filterAutocompleteProveedor(razonSocial as string)
          : this.proveedores.slice();
      }),
    );

    this.ingresoForm
      .get(['insumoAlmacen', 'costo'])
      ?.valueChanges.pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((value) => {
        if (
          (value !== '' &&
            this.foodItemControl.value.precioReferencial !== 0 &&
            value >
              this.foodItemControl.value.precioReferencial *
                1.1 *
                this.ingresoForm.value.insumoAlmacen.cantidad) ||
          value <
            this.foodItemControl.value.precioReferencial *
              0.9 *
              this.ingresoForm.value.insumoAlmacen.cantidad
        ) {
          this.isWarning = true;
          this.ingresoForm
            .get(['insumoAlmacen', 'costo'])
            ?.setErrors({ fueraDeRango: true });
        } else {
          this.isWarning = false;
        }
      });
  }
  ngOnDestroy(): void {
    this._unsubscribeOnDestroy$.next(true);
    this._unsubscribeOnDestroy$.complete();
  }
  triggerResize() {
    this._ngZone.onStable
      .pipe(take(1))
      .subscribe(() => this.autosizeObservationField.resizeToFitContent(true));
  }

  getIngresoById(id: string) {
    this.alimentosService
      .queryGetIngreso({ id })
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((response) => {
        this.inflow = response.getIngreso;
        this.setValuesForms();
        return this.inflow;
      });
  }

  private _determineInflowType():
    | 'Con documentos contables'
    | 'Sin Factura'
    | 'Sin File' {
    if (this.inflow == null) {
      this.notificationService.notifyError(
        'No se pudo cargar el ingreso. Por favor, intente nuevamente. Si el error persiste, contacte al administrador del sistema.',
      );
      return 'Con documentos contables';
    }

    if (!this.inflow?.fileNumber && !this.inflow?.numFactura) {
      return 'Sin File';
    }

    if (!this.inflow?.fileNumber && this.inflow?.numFactura) {
      return 'Con documentos contables';
    }

    if (this.inflow?.fileNumber && !this.inflow?.numFactura) {
      return 'Sin Factura';
    }

    return 'Con documentos contables';
  }

  /**
   * Populates the form controls and selected inflow type using the current inflow data.
   *
   * @returns {void} No return value.
   */
  setValuesForms() {
    if (!this.inflow) {
      this.notificationService.notifyError(
        'No se pudo cargar el ingreso. Por favor, intente nuevamente. Si el error persiste, contacte al administrador del sistema.',
      );
      return;
    }

    this.selectedFoodInflowType = this._determineInflowType();

    console.log({
      inflow: this.inflow,
    });

    const proveedorActual = this.proveedores.find(
      (provider: any) =>
        provider.id === (this.inflow?.proveedor?.idProveedor ?? ''),
    );

    this.providerControl.setValue(proveedorActual);

    if (!proveedorActual) this.providerControl.setValue(this.inflow.proveedor);

    this.ingresoForm
      .get(['razonSocial'])
      ?.setValue(this.inflow.proveedor?.razonSocial);

    this.ingresoForm.get(['ruc'])?.setValue(this.inflow.proveedor?.ruc);
    this.ingresoForm.get(['numFactura'])?.setValue(this.inflow.numFactura);
    this.ingresoForm.get(['fechaFactura'])?.setValue(this.inflow.fechaFactura);
    this.ingresoForm
      .get(['fechaRecibo'])
      ?.setValue(this.inflow?.fechaRecibo || this.inflow?.acquisitionDate);
    this.ingresoForm.get(['numRecibo'])?.setValue(this.inflow.numRecibo);
    this.ingresoForm.get(['fileNumber'])?.setValue(this.inflow.fileNumber);
    this.ingresoForm.get(['idItinerary'])?.setValue(this.inflow?.idItinerary);
    this.ingresoForm.get(['observation'])?.setValue(this.inflow.observation);

    if (this.inflow?.fileNumber) {
      console.log({
        inflow: this.inflow,
      });
      const matchingFile = this.filesNumbers.find(
        (fileOption) => fileOption.id === this.inflow?.idItinerary,
      );
      const fileData = matchingFile || {
        id: this.inflow?.idItinerary,
        fileNumber: this.inflow.fileNumber,
        fileExtend: null,
      };
      this.fileControl.setValue(fileData);
    }

    this.ubicacionControl.setValue(this.inflow?.locacion || '');

    const activeDetalleInsumos =
      this.inflow.detalleInsumos?.filter((detalle) => !detalle.deletedAt) || [];
    activeDetalleInsumos.forEach((detalle) => {
      this.addedIngreso.push({
        id: detalle.id,
        nombreInsumo: detalle.nombreInsumo,
        unidadMedida: detalle.unidadMedida,
        cantidad: detalle.cantidad,
        costo: detalle.costo,
        categoria: detalle.categoria,
      });
    });
    this.dataSource = new MatTableDataSource(activeDetalleInsumos);
    this.captureInitialStateSnapshot();
    this.cdRef.markForCheck();
  }

  private initialInflowStateSnapshot: string = '';

  /**
   * Captures the initial state snapshot of the inflow form controls and items list for dirty checking.
   *
   * @description Serializes form values, controls, and active addedIngreso items to JSON for comparison.
   * @returns {void} No return value.
   * @throws {never} This method does not throw errors.
   */
  captureInitialStateSnapshot(): void {
    const statePayload = {
      formValues: this.ingresoForm.getRawValue(),
      ubicacion: this.ubicacionControl.value || '',
      provider: this.providerControl.value || null,
      file: this.fileControl.value || null,
      inflowType: this.selectedFoodInflowType,
      items: this.addedIngreso.map((item) => ({
        id: item.id || item.nombreInsumo,
        nombreInsumo: item.nombreInsumo,
        unidadMedida: item.unidadMedida,
        cantidad: Number(item.cantidad || 0),
        costo: Number(item.costo || 0),
      })),
    };
    this.initialInflowStateSnapshot = JSON.stringify(statePayload);
  }

  /**
   * Compares current form state against the initial snapshot to detect if user made any edits.
   *
   * @returns {boolean} True if the inflow form or items list has been edited, false otherwise.
   */
  hasIngresoBeenEdited(): boolean {
    if (!this.isEditMode) return true;

    const currentStatePayload = {
      formValues: this.ingresoForm.getRawValue(),
      ubicacion: this.ubicacionControl.value || '',
      provider: this.providerControl.value || null,
      file: this.fileControl.value || null,
      inflowType: this.selectedFoodInflowType,
      items: this.addedIngreso.map((item) => ({
        id: item.id || item.nombreInsumo,
        nombreInsumo: item.nombreInsumo,
        unidadMedida: item.unidadMedida,
        cantidad: Number(item.cantidad || 0),
        costo: Number(item.costo || 0),
      })),
    };

    return (
      JSON.stringify(currentStatePayload) !== this.initialInflowStateSnapshot
    );
  }

  private _filterAutocompleteFoodItem(nombreInsumo: string): any[] {
    const filterValue = nombreInsumo.toLowerCase();

    return this.foodItems.filter((foodItem) =>
      foodItem.nombreInsumo?.toLowerCase().includes(filterValue),
    );
  }

  private _filterAutocompleteProveedor(razonSocial: string): any[] {
    const filterValue = razonSocial?.toLowerCase();

    return this.proveedores.filter((option: { razonSocial: string }) =>
      option.razonSocial?.toLowerCase().includes(filterValue),
    );
  }

  /**
   * Filters the file numbers autocomplete list matching the user input string.
   *
   * @description Checks if the user search term matches the base file number,
   * the extended file number (e.g., 202601001-A), or the combined code.
   *
   * @param {string} searchInput - User-entered search string.
   * @returns {Array<{ id: string; fileNumber: string; fileExtend?: string | null }>} Filtered list of files.
   */
  private _filterAutocompleteFile(
    searchInput: string,
  ): Array<{ id: string; fileNumber: string; fileExtend?: string | null }> {
    const filterValue = (searchInput || '').toLowerCase().trim();
    if (!filterValue) {
      return this.filesNumbers.slice();
    }
    return this.filesNumbers.filter((fileOption) => {
      const baseFileNumber = fileOption?.fileNumber?.toLowerCase() || '';
      const fullExtendedFile = fileOption?.fileExtend
        ? `${baseFileNumber}-${fileOption.fileExtend.toLowerCase()}`
        : baseFileNumber;
      const compactExtendedFile = fileOption?.fileExtend
        ? `${baseFileNumber}${fileOption.fileExtend.toLowerCase()}`
        : baseFileNumber;
      return (
        baseFileNumber.includes(filterValue) ||
        fullExtendedFile.includes(filterValue) ||
        compactExtendedFile.includes(filterValue)
      );
    });
  }

  /**
   * Formats the display label for an itinerary file option with optional extension.
   *
   * Description:
   *   Produces hyphenated label (e.g., '202601001-A') if fileExtend exists, or base fileNumber otherwise.
   *
   * Parameters:
   *   file: { id?: string; fileNumber: string; fileExtend?: string | null } | string | null | undefined - Target file entity or string.
   *
   * Return Value:
   *   string - Formatted file display label string.
   *
   * Exceptions & Errors:
   *   Handles null/undefined values safely returning empty string.
   */
  getFileNumberText = (
    file:
      | { id?: string; fileNumber: string; fileExtend?: string | null }
      | string
      | null
      | undefined,
  ): string => {
    if (!file) return '';
    if (typeof file === 'string') return file;
    return file.fileExtend
      ? `${file.fileNumber}-${file.fileExtend}`
      : file.fileNumber || '';
  };

  /**
   * Handles selection of a file from the autocomplete options.
   *
   * Description:
   *   Updates the file control value and syncs fileNumber and idItinerary into the main ingresoForm.
   *
   * Parameters:
   *   event: MatAutocompleteSelectedEvent - Material autocomplete selected event payload.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  onFileNumberSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedFile = event.option.value;
    if (selectedFile) {
      this.fileControl.setValue(selectedFile);
      this.ingresoForm.patchValue({
        fileNumber: selectedFile.fileNumber,
        idItinerary: selectedFile.id,
      });
    }
  }

  addFoodItemSku() {
    const insumoData = this.ingresoForm.get('insumoAlmacen') as FormGroup;

    if (this.foodItemControl.value && insumoData) {
      const foodItem = {
        id: this.foodItemControl.value.id,
        nombreInsumo: this.foodItemControl.value.nombreInsumo,
        unidadMedida: this.foodItemControl.value.unidadMedida,
        cantidad: this.ingresoForm.value.insumoAlmacen.cantidad,
        costo: this.ingresoForm.value.insumoAlmacen.costo,
        categoria: this.foodItemControl.value.categoria,
      };

      this.addedIngreso.push(foodItem);

      this.dataSource = new MatTableDataSource(this.addedIngreso);

      const insumoAlmacenFormGroup = this.ingresoForm.get(
        'insumoAlmacen',
      ) as FormGroup;
      insumoAlmacenFormGroup.reset();

      this.foodItemControl.reset();
      this.isWarning = false;
      this.ingresoForm.get(['insumoAlmacen', 'costo'])?.setErrors(null);
    } else {
      Swal.fire({
        toast: true,
        position: 'top-end',
        title: 'Falta ingresar campos requeridos',
        icon: 'warning',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  }

  getOptionText(option: any) {
    return option.nombreInsumo;
  }

  getOptionTextProveedor(option: any) {
    return option?.razonSocial;
  }

  openDialogModify(inflowIndex: number): void {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: `Modificar Insumo ${this.addedIngreso[inflowIndex].nombreInsumo}`,
      cantidad: this.addedIngreso[inflowIndex].cantidad,
      costo: this.addedIngreso[inflowIndex].costo,
    };
    dialogConfig.width = '50%';
    let dialogRef = this.dialog.open(
      DialogIngresoInsumoComponent,
      dialogConfig,
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe({
        next: ({ cantidad = null, costo = null }) => {
          if (cantidad && costo) {
            this.addedIngreso[inflowIndex].cantidad = cantidad;
            this.addedIngreso[inflowIndex].costo = costo;
            this.dataSource.data = this.dataSource.data.map(
              (foodItem, foodItemIndex) => {
                if (foodItemIndex === inflowIndex) {
                  return { ...foodItem, cantidad, costo };
                }
                return foodItem;
              },
            );
          }

          if (cantidad && !costo) {
            this.addedIngreso[inflowIndex].cantidad = cantidad;
            this.dataSource.data = this.dataSource.data.map(
              (foodItem, foodItemIndex) => {
                if (foodItemIndex === inflowIndex) {
                  return { ...foodItem, cantidad };
                }
                return foodItem;
              },
            );
          }
          if (costo && !cantidad) {
            this.addedIngreso[inflowIndex].costo = costo;
            this.dataSource.data = this.dataSource.data.map(
              (foodItem, foodItemIndex) => {
                if (foodItemIndex === inflowIndex) {
                  return { ...foodItem, costo };
                }
                return foodItem;
              },
            );
          }
        },
        error: (err) => {
          console.error('Error al modificar insumo:', err);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo modificar el insumo',
          });
        },
      });
  }

  getMissingFields(): string[] {
    const missingFields: string[] = [];

    if (!this.ubicacionControl.value) {
      missingFields.push('Ubicación');
    }

    if (this.addedIngreso.length === 0) {
      missingFields.push('Insumos agregados');
    }

    if (this.selectedFoodInflowType === 'Con documentos contables') {
      if (!this.providerControl.value) {
        missingFields.push('Proveedor');
      }
      if (!this.ingresoForm.value.ruc) {
        missingFields.push('RUC');
      }
      if (!this.ingresoForm.value.numFactura) {
        missingFields.push('Número de Factura');
      }
      if (!this.ingresoForm.value.fechaFactura) {
        missingFields.push('Fecha de Factura');
      }
    } else if (this.selectedFoodInflowType === 'Sin Factura') {
      if (!this.providerControl.value) {
        missingFields.push('Proveedor');
      }
      console.log({
        fileControlValue: this.fileControl.value,
      });
      if (!this.fileControl.value?.fileNumber && !this.fileControl.value?.id) {
        missingFields.push('Número de File correcto');
      }
      if (!this.ingresoForm.value.numRecibo) {
        missingFields.push('Número de Recibo');
      }
      if (!this.ingresoForm.value.fechaRecibo) {
        missingFields.push('Fecha de Recibo');
      }
    } else if (this.selectedFoodInflowType === 'Sin File') {
      if (!this.ingresoForm.value.fechaRecibo) {
        missingFields.push('Fecha de adquisición');
      }
      if (!this.ingresoForm.value.observation) {
        missingFields.push('Observación');
      }
    }

    return missingFields;
  }

  /**
   * Submits new food inflow record to backend, triggering Kardex movement recording and stock updates.
   *
   * @returns {void} No return value.
   */
  addIngreso() {
    const missingFields = this.getMissingFields();

    if (missingFields.length === 0) {
      const foodItemInflow = {
        hasAccountingDocuments: this.ingresoForm.value.hasAccountingDocuments,
        proveedor: {
          razonSocial: this.providerControl.value?.razonSocial || '',
          ruc: this.ingresoForm.value.ruc || '',
          idProveedor: this.providerControl.value?.id || '',
        },
        locacion: this.ubicacionControl.value,
        numFactura: this.ingresoForm.value.numFactura || '',
        fechaFactura: this.ingresoForm.value.fechaFactura || '',
        detalleInsumos: this.addedIngreso,
        fileNumber:
          (typeof this.fileControl.value === 'object'
            ? this.fileControl.value?.fileNumber
            : this.fileControl.value) ||
          this.ingresoForm.value.fileNumber ||
          '',
        idItinerary:
          (typeof this.fileControl.value === 'object'
            ? this.fileControl.value?.id
            : null) ||
          this.ingresoForm.value.idItinerary ||
          '',
        numRecibo: this.ingresoForm.value.numRecibo || '',
        fechaRecibo:
          this.ingresoForm.value?.fechaRecibo ??
          this.ingresoForm.value.acquisitionDate,
        observation: this.ingresoForm.value?.observation || '',
      };

      console.log({
        ingresoFormValue: this.ingresoForm.value,
      });

      this.alimentosService
        .mutationCreateFoodInflow(foodItemInflow)
        .pipe(takeUntil(this._unsubscribeOnDestroy$))
        .subscribe(() => {
          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: 'El ingreso se registró satisfactoriamente',
            showConfirmButton: false,
            timer: 4000,
          });

          this.router
            .navigate(['/gestion/ingresos'])
            .then(() => console.log('Navigated to Ingreso insumos'));
        });
      return;
    }

    Swal.fire({
      toast: true,
      position: 'top-end',
      title: `Faltan campos requeridos: ${missingFields.join(', ')}`,
      icon: 'warning',
      showConfirmButton: false,
      timer: 4000,
    });
  }
  removeFoodItemFromInflow(foodItem: FoodItemModel, i: number): void {
    Swal.fire({
      title: `¿Está seguro que desea eliminar este insumo (${
        foodItem?.nombreInsumo || '-'
      }) del Ingreso?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí',
      confirmButtonColor: '#E95A1A',
      cancelButtonText: 'No',
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: 'El registro se eliminó satisfactoriamente.',
          showConfirmButton: false,
          timer: 4000,
        });
        this.dataSource.data.splice(i, 1);
        this.dataSource._updateChangeSubscription();
        this.addedIngreso = this.dataSource.data;
      } else if (result.dismiss === Swal.DismissReason.cancel) {
      }
    });
  }
  onSelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.ingresoForm
      .get(['insumoAlmacen', 'unidadMedida'])
      ?.setValue(event.option.value.unidadMedida);
  }

  onProveedorTyped(event: Event): void {
    const input = (event.target as HTMLInputElement).value;

    // Si no se seleccionó nada, solo se está escribiendo texto manualmente
    const current = this.providerControl.value;
    if (!current || typeof current !== 'object' || !current.razonSocial) {
      this.providerControl.setValue({
        razonSocial: input,
      });
      // Limpiar ruc si es un proveedor nuevo no seleccionado
      this.ingresoForm.patchValue({
        ruc: '',
      });
    } else {
      // Si ya hay un objeto, solo actualizamos razonSocial
      this.providerControl.setValue({
        ...current,
        razonSocial: input,
      });
    }
  }

  onSelectionProviderChanged(event: MatAutocompleteSelectedEvent): void {
    const selected = event.option.value;

    this.providerControl.setValue({
      razonSocial: selected.razonSocial,
      ruc: selected.ruc,
      id: selected.id,
    });

    this.ingresoForm.get('ruc')?.setValue(selected.ruc);
  }

  onRazonSocialInput(event: Event): void {
    const razonSocial = (event.target as HTMLInputElement).value;

    const current = this.providerControl.value;
    this.providerControl.setValue({
      ...(typeof current === 'object' && current !== null ? current : {}),
      razonSocial: razonSocial,
    });
  }

  /**
   * Submits updated food inflow payload to backend, updating active SKUs and Kardex movements.
   *
   * @returns {void} No return value.
   */
  updateIngreso() {
    const missingFields = this.getMissingFields();

    if (missingFields.length === 0) {
      this.isLoading = true;
      this.cdRef.markForCheck();

      const foodInflowToUpdate = {
        hasAccountingDocuments: this.ingresoForm.value.hasAccountingDocuments,
        proveedor: {
          razonSocial: this.providerControl.value?.razonSocial || '',
          ruc: this.ingresoForm.value.ruc || '',
          idProveedor: this.providerControl.value?.id || '',
        },
        locacion: this.ubicacionControl.value,
        numFactura: this.ingresoForm.value.numFactura || '',
        fechaFactura: this.ingresoForm.value.fechaFactura || '',
        detalleInsumos: this.addedIngreso,

        fileNumber:
          (typeof this.fileControl.value === 'object'
            ? this.fileControl.value?.fileNumber
            : this.fileControl.value) ||
          this.ingresoForm.value.fileNumber ||
          '',
        idItinerary:
          (typeof this.fileControl.value === 'object'
            ? this.fileControl.value?.id
            : null) ||
          this.ingresoForm.value.idItinerary ||
          '',

        numRecibo: this.ingresoForm.value.numRecibo || '',
        fechaRecibo:
          this.ingresoForm.value?.fechaRecibo ||
          this.ingresoForm.value?.acquisitionDate,
        observation: this.ingresoForm.value?.observation || '',
      };

      console.log({
        foodInflowToUpdate,
      });

      this.alimentosService
        .mutationUpdateIngreso({
          id: this?.ingresoId ?? '',
          almacenInsumo: foodInflowToUpdate,
        })
        .pipe(takeUntil(this._unsubscribeOnDestroy$))
        .subscribe({
          next: (data: any) => {
            this.isLoading = false;
            this.captureInitialStateSnapshot();
            this.cdRef.markForCheck();
            Swal.fire({
              title: `Se modificó el ingreso exitosamente. \n¿Desea regresar a la lista de ingresos?`,
              position: 'top-end',
              icon: 'success',
              showConfirmButton: true,
              showDenyButton: true,
            }).then((result) => {
              if (result.isConfirmed) {
                this.router.navigate([`/gestion/ingresos`]);
              }
              if (result.isDenied) {
                Swal.fire({
                  title: `Puede seguir editando el ingreso`,
                  position: 'top-end',
                  icon: 'info',
                  showConfirmButton: true,
                });
              }
            });
          },
          error: (err) => {
            console.error(err);
            this.isLoading = false;
            this.cdRef.markForCheck();
            Swal.fire({
              title: `Error al modificar el ingreso`,
              position: 'top-end',
              icon: 'error',
              showConfirmButton: true,
            });
          },
        });
      return;
    }

    Swal.fire({
      toast: true,
      position: 'top-end',
      title: `Faltan campos requeridos: ${missingFields.join(', ')}`,
      icon: 'warning',
      showConfirmButton: false,
      timer: 4000,
    });
  }
}
